import { useRouter } from 'next/router';
import {
  appendGOATandCountryCodeToTitle,
  getParentCategoryBasePath,
} from 'shared/utils/pageMetadataUtils';
import { getBrandParentMetadata } from 'shared/utils/getBrandParentMetadata';
import { getBrandCategoryMetadata } from 'shared/utils/getBrandCategoryMetadata';
import { getSilhouetteMetadata } from 'shared/utils/getSilhouetteMetadata';
import { getColorValueMetadata } from 'shared/utils/getColorValueMetadata';
import { getDesignerValueMetadata } from 'shared/utils/getDesignerValueMetadata';
import { getUpperMaterialValueMetadata } from 'shared/utils/getUpperMaterialValueMetadata';
import { getTechnologyValueMetadata } from 'shared/utils/getTechnologyValueMetadata';
import { getCollectionsMetadata } from 'shared/utils/getCollectionsMetadata';
import { getParentCategoryMetadata } from 'shared/utils/getParentCategoryMetadata';
import { getSubcategoryMetadata } from 'shared/utils/getSubcategoryMetadata';
import { getPdpMetadata } from 'shared/utils/getPdpMetadata';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { IProductTemplate } from 'shared/types/ProductTemplate';
import { getTimelineMetadata } from 'shared/utils/getTimelineMetadata';
import { PLP_SUBCATEGORIES } from 'shared/enums/category';

interface IUsePageMetadata {
  mainTitle?: string;
  defaultTitle?: string;
  mainDescription?: string;
  constructorResponse?: IConstructorBrowseResponse;
  productTemplate?: IProductTemplate;
}

export const usePageMetadata = ({
  mainTitle,
  defaultTitle,
  mainDescription,
  constructorResponse,
  productTemplate,
}: IUsePageMetadata) => {
  const { locale, pathname, query } = useRouter();

  let pageTitle = appendGOATandCountryCodeToTitle(mainTitle, locale);
  const pageDescription = mainDescription;

  const dataToConstructMetadata = {
    pageTitle,
    pageDescription,
    constructorResponse,
    query,
    locale,
    pathname,
    productTemplate,
  };

  if (pathname.includes('collections') && query?.slug && constructorResponse) {
    return getCollectionsMetadata(dataToConstructMetadata);
  }

  if (pathname.includes('brand') && query?.slug && constructorResponse) {
    return getBrandParentMetadata(dataToConstructMetadata);
  }

  if (pathname.includes('timeline') && query?.timelineYear && constructorResponse) {
    return getTimelineMetadata(dataToConstructMetadata);
  }

  const pathnameSplit = pathname?.split('/');
  if (
    (pathnameSplit?.[1] === 'sneakers' || pathnameSplit?.[1] === 'apparel') &&
    PLP_SUBCATEGORIES.has(pathnameSplit?.[2])
  ) {
    return getSubcategoryMetadata(dataToConstructMetadata);
  }

  if (query?.pageSlug && Object.keys(query).length > 1 && constructorResponse) {
    if (query?.brandName) {
      return getBrandCategoryMetadata(dataToConstructMetadata);
    }

    if (query?.silhouette) {
      return getSilhouetteMetadata(dataToConstructMetadata);
    }

    if (query?.colorValue) {
      return getColorValueMetadata(dataToConstructMetadata);
    }
  }

  if (productTemplate) {
    return getPdpMetadata(dataToConstructMetadata);
  }

  if (query?.designerValue) {
    return getDesignerValueMetadata(dataToConstructMetadata);
  }

  if (query?.upperMaterialValue) {
    return getUpperMaterialValueMetadata(dataToConstructMetadata);
  }

  if (query?.technologyValue) {
    return getTechnologyValueMetadata(dataToConstructMetadata);
  }

  const { isCategoryBasePath, parentCategory } = getParentCategoryBasePath(pathname);
  if (isCategoryBasePath && constructorResponse) {
    return getParentCategoryMetadata({ ...dataToConstructMetadata, parentCategory });
  }

  if (!pageTitle) {
    pageTitle = appendGOATandCountryCodeToTitle(defaultTitle, locale);
  }

  return { pageTitle, pageDescription };
};
